import sidekickLogo from "@assets/sidekick-logo.svg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { Button, Skeleton, Typography } from "@mui/material";
import type {
  PromptLibraryCategory,
  PromptLibraryItem,
} from "@shared/apis/sidekick/generated";
import { SvgIcon } from "@shared/components/SvgIcon";
import {
  BackRow,
  Card,
  Container,
  HeaderIconContainer,
  Row,
  RowIconContainer,
  RowLabel,
  SidekickLogoImg,
  ViewHeader,
  ViewTitle,
  ViewWrapper,
} from "./styles";
import { useNavigate } from "react-router";
import { useRef, useState } from "react";
import { SwitchTransition, Transition } from "react-transition-group";
import { pickRandomItem } from "@shared/utils/pick-random-item";

const TRANSITION_DURATION = 100;

const TRANSITION_STYLES: Record<string, React.CSSProperties> = {
  entering: { opacity: 0 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

type Props = {
  categories: PromptLibraryCategory[];
  onItemClick: (item: PromptLibraryItem) => void;
  disabled?: boolean;
  /** When true, omits grid-area so the block can sit inside another layout (e.g. Focus page columns). */
  embedded?: boolean;
};

export const QuickCategories = ({
  categories,
  onItemClick,
  disabled,
  embedded = false,
}: Props) => {
  const [selectedCategory, setSelectedCategory] =
    useState<PromptLibraryCategory | null>(null);
  const navigate = useNavigate();
  const viewRef = useRef<HTMLDivElement>(null);

  const transitionKey = selectedCategory?.id ?? "root";

  return (
    <Container $disabled={disabled} $embedded={embedded}>
      <SwitchTransition mode="out-in">
        <Transition
          key={transitionKey}
          nodeRef={viewRef}
          timeout={TRANSITION_DURATION}
        >
          {(state) => (
            <ViewWrapper
              ref={viewRef}
              style={{
                transition: `opacity ${TRANSITION_DURATION}ms ease`,
                ...TRANSITION_STYLES[state],
              }}
            >
              {selectedCategory ? (
                <>
                  <ViewHeader>
                    <HeaderIconContainer>
                      <SvgIcon name={selectedCategory.icon} fontSize="large" />
                    </HeaderIconContainer>
                    <ViewTitle variant="h4">{selectedCategory.label}</ViewTitle>
                  </ViewHeader>
                  <Card>
                    <BackRow
                      onClick={() => setSelectedCategory(null)}
                      size="small"
                      variant="text"
                      startIcon={<ArrowBackIcon fontSize="small" />}
                    >
                      <Typography variant="inherit">Back</Typography>
                    </BackRow>
                    {selectedCategory.subcategories
                      .filter((sub) => (sub.items?.length ?? 0) > 0)
                      .map((sub) => (
                        <Row
                          key={sub.id}
                          onClick={() => {
                            const picked = pickRandomItem(sub.items ?? []);
                            if (picked) {
                              onItemClick(picked);
                            }
                          }}
                        >
                          <RowIconContainer>
                            <SvgIcon
                              name={sub.icon || "ElectricBolt"}
                              fontSize="small"
                            />
                          </RowIconContainer>
                          <RowLabel>{sub.label}</RowLabel>
                        </Row>
                      ))}
                  </Card>
                </>
              ) : (
                <>
                  <ViewHeader>
                    <SidekickLogoImg src={sidekickLogo} alt="Sidekick" />
                    <ViewTitle variant="h4">
                      What can I help you get done?
                    </ViewTitle>
                  </ViewHeader>
                  <Card>
                    {categories.map((category) => (
                      <Row
                        key={category.id}
                        onClick={() => setSelectedCategory(category)}
                      >
                        <RowIconContainer>
                          <SvgIcon name={category.icon} fontSize="small" />
                        </RowIconContainer>
                        <RowLabel>{category.label}</RowLabel>
                      </Row>
                    ))}
                  </Card>
                  <Button
                    startIcon={<TravelExploreIcon />}
                    onClick={() => navigate("/explore")}
                    size="small"
                    variant="outlined"
                  >
                    Explore
                  </Button>
                </>
              )}
            </ViewWrapper>
          )}
        </Transition>
      </SwitchTransition>
    </Container>
  );
};

export const QuickCategoriesSkeleton = ({
  embedded = false,
}: {
  embedded?: boolean;
}) => {
  return (
    <Container $embedded={embedded}>
      <ViewWrapper>
        <ViewHeader>
          <Skeleton variant="circular" width={56} height={56} />
          <Skeleton variant="rounded" width="80%" height={40} />
        </ViewHeader>
        <Card>
          {Array(5)
            .fill(null)
            .map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={52}
                sx={{ borderRadius: "0.625rem", width: "100%" }}
              />
            ))}
        </Card>
      </ViewWrapper>
    </Container>
  );
};
